import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { SinatraState } from '@rsos/sinatra';
import normalizeError from '@rsos/sinatra/src/utils/normalizeError';
import { organizationSelector } from '../selectors/organizationSelector';
import { KeywordAPI } from './KeywordAPI';
import type { KeywordSet } from './KeywordSet';

/**
 * Because of how keywords are currently stored on the backend, in a json blob,
 * there are actually only two true actions: fetching and updating the entire
 * list. For more granular actions built from these fundamentals, see
 * KeywordSet.ts
 */
export const fetchKeywordSets = createAsyncThunk<
  KeywordSet[],
  void,
  { state: { sinatra: SinatraState } }
>('keywordSets/fetchKeywordSets', async (_, { rejectWithValue, getState }) => {
  const orgName = organizationSelector(getState())?.name;
  if (!orgName) return rejectWithValue('No organization to fetch from');
  try {
    const api = new KeywordAPI(orgName);
    return api.fetchKeywordSets();
  } catch (err) {
    const { message } = normalizeError(err);
    return rejectWithValue(message);
  }
});

export const updateKeywordSets = createAsyncThunk<
  KeywordSet[],
  KeywordSet[],
  { state: { sinatra: SinatraState } }
>(
  'keywordSets/updateKeywordSets',
  async (keywordSets, { rejectWithValue, getState }) => {
    const orgName = organizationSelector(getState())?.name;
    if (!orgName) return rejectWithValue('No organization to fetch from');
    try {
      const api = new KeywordAPI(orgName);
      return api.saveKeywordSets(keywordSets);
    } catch (err) {
      const { message } = normalizeError(err);
      return rejectWithValue(message);
    }
  },
);

export type KeywordSetsState = {
  loading: boolean;
  loadingError: null | string;
  saving: boolean;
  savingError: null | string;
  keywordSets?: KeywordSet[];
};

const initialState: KeywordSetsState = {
  loading: false,
  loadingError: null,
  saving: false,
  savingError: null,
  keywordSets: undefined,
};

const keywordSetsSlice = createSlice({
  name: 'keywordSets',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchKeywordSets.fulfilled, (state, action) => {
        state.keywordSets = [...action.payload];
        state.loading = false;
        state.loadingError = null;
      })
      .addCase(fetchKeywordSets.pending, state => {
        state.loading = true;
      })
      .addCase(fetchKeywordSets.rejected, (state, { error = {} }) => {
        state.loading = false;
        state.loadingError = error.message || 'Loading keywords failed.';
      })

      .addCase(updateKeywordSets.fulfilled, (state, action) => {
        state.keywordSets = [...action.payload];
        state.saving = false;
        state.savingError = null;
      })
      .addCase(updateKeywordSets.pending, state => {
        state.saving = true;
      })
      .addCase(updateKeywordSets.rejected, (state, { error = {} }) => {
        state.saving = false;
        state.loadingError = error.message || 'Saving keywords failed.';
      });
  },
});

export const keywordSetsSelector = (state: { keywordSets: KeywordSetsState }) =>
  state.keywordSets;

export const keywordsSetsReducer = keywordSetsSlice.reducer;
